/** SP7 §7 — the one-line "on accept" readout. Pure string derivation from the
 *  live derivation; wording is pinned by policy-explain.test.ts (an operator-
 *  facing contract — change the tests deliberately, never incidentally). */
import type { RemittanceAllocationConfig } from "@capture/shared";
import type { LiveDerivation } from "./live-balance";
import { formatMoneyCents } from "./money";

const money = (cents: number): string => formatMoneyCents(Math.abs(cents));
const sideWord = (side: "shortPay" | "overPay") => (side === "shortPay" ? "short-pay" : "overpay");

export function explainPolicyOutcome(input: {
  live: LiveDerivation;
  targets?: RemittanceAllocationConfig;
  overridden: boolean;
}): string | null {
  const { snapshot, outcome } = input.live;

  const hard = (snapshot.ruleFlags ?? []).filter((f) => f.severity === "hard");
  if (hard.length > 0 && !input.overridden) {
    return `Accept will be blocked by rule: ${hard.map((f) => f.message).join("; ")}`;
  }

  if (snapshot.adjustment) {
    const a = snapshot.adjustment;
    const amount = money(a.deltaCents);
    // `via` (SP7) is the engine's own record of HOW the delta resolved —
    // authoritative when present. The shape heuristics below it exist only for
    // snapshots persisted before `via` shipped.
    const via =
      a.via ??
      (outcome?.couponAdjustments.length
        ? "largest-remainder"
        : a.ref.startsWith("target:") ||
            (a.resolution === "rule" && outcome?.policyRows.length === 1 && outcome.policyRows[0]!.amountCents === a.deltaCents)
          ? "target"
          : "pro-rata-allocations");
    if (via === "largest-remainder") {
      return `On accept: ${sideWord(a.side)} ${amount} → largest-remainder across ${outcome?.couponAdjustments.length ?? 0} coupons`;
    }
    if (via === "target") {
      const targetId = outcome?.policyRows[0]?.targetId ?? a.ref.slice("target:".length);
      const label = input.targets?.targets.find((t) => t.id === targetId)?.label ?? targetId;
      const how = a.resolution === "rule" ? `policy rule ${Number(a.ref.slice("rule:".length)) + 1}` : "default target";
      return `On accept: ${sideWord(a.side)} ${amount} → allocated to ${label} (${how})`;
    }
    return `On accept: ${sideWord(a.side)} ${amount} → pro-rata across allocations`;
  }

  if (snapshot.policyFlag) {
    return `In-policy ${sideWord(snapshot.policyFlag)} flagged — accept stays blocked until balanced or overridden`;
  }
  return null;
}
